import { useEffect, useState } from "react";
import { DragDropContext, Draggable, DropResult, Droppable } from "react-beautiful-dnd";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import DstButton from "../../../../components/DstButton/DstButton";
import Popup from "../../../../components/Popup/Popup";
import translate from "../../../../services/Translate";
import { JobBlock } from "../../LandingPageJobBlocks.d";
import { reorderJobBlocks } from "../../LandingPageJobBlocks.function";
import { ReorderJobBlocksModalProps } from "./ReorderJobBlocksModal.d";

const ReorderJobBlocksModal = ({ show, onClose, jobBlocks, onSaved, language }: ReorderJobBlocksModalProps) => {
  const [orderedBlocks, setOrderedBlocks] = useState<JobBlock[]>([]);

  useEffect(() => {
    setOrderedBlocks([...jobBlocks]);
  }, [jobBlocks]);

  const _onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const nextOrder = Array.from(orderedBlocks);
    const [movedBlock] = nextOrder.splice(result.source.index, 1);
    nextOrder.splice(result.destination.index, 0, movedBlock);

    setOrderedBlocks(nextOrder);
  };

  const _saveJobBlocksOrder = () => {
    const orderedIds = orderedBlocks.map((jobBlock) => jobBlock.id);

    reorderJobBlocks(orderedIds, language, () => {
      onSaved();
      onClose();
    });
  };

  return (
    <Popup
      show={show}
      onClose={onClose}
      modalTitle={translate(language, "PAGES.LANDING_PAGE_JOB_BLOCKS.REORDER_MODAL.TITLE")}
      size="lg"
    >
      <DragDropContext onDragEnd={_onDragEnd}>
        <Droppable droppableId="job-blocks">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {orderedBlocks.map((jobBlock, blockIndex) => (
                <Draggable key={jobBlock.id} draggableId={jobBlock.id} index={blockIndex}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className="p-3 border rounded d-flex align-items-center mb-2 off-white-background"
                      style={{
                        ...provided.draggableProps.style,
                        opacity: snapshot.isDragging ? 0.8 : 1,
                      }}
                    >
                      <span className="dst-icon-menu-01 fw-bold cursor-grab me-3" />
                      <span className="d-flex align-items-center">
                        {jobBlock.title}
                        {jobBlock.deprecated && (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-deprecated-${jobBlock.id}`}>
                                {translate(language, "GLOBAL.DEPRECATED")}
                              </Tooltip>
                            }
                          >
                            <span className="dst-icon dst-icon-alert-circle ms-2" />
                          </OverlayTrigger>
                        )}
                      </span>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <div className="d-flex justify-content-end gap-2 mt-4">
        <DstButton value={translate(language, "GLOBAL.CANCEL")} variant="secondary" clickFunction={onClose} />
        <DstButton
          value={translate(language, "PAGES.LANDING_PAGE_JOB_BLOCKS.REORDER_MODAL.BUTTON.SAVE_ORDER")}
          clickFunction={_saveJobBlocksOrder}
        />
      </div>
    </Popup>
  );
};

export default ReorderJobBlocksModal;
